const imageBytes = new Map<string, Promise<string>>();

export function displayedImageUrl(href: string, base: string): string | null {
  try { const url = new URL(href, base), origin = new URL(base).origin; return url.origin !== 'null' && url.origin === origin && ['http:','https:','blob:'].includes(url.protocol) ? url.href : null; } catch { return null; }
}

async function inlineDisplayedImage(href: string): Promise<string> {
  if (href.startsWith('data:image/')) return href;
  const url = displayedImageUrl(href, location.href);
  if (!url) throw new Error('This scene image cannot be sampled.');
  let pending = imageBytes.get(url);
  if (!pending) {
    pending = (async () => {
      const response = await fetch(url, { credentials: 'same-origin', redirect: 'error' });
      if (!response.ok) throw new Error('The scene image could not be loaded.');
      const blob = await response.blob();
      if (!blob.type.startsWith('image/')) throw new Error('The scene image format is unavailable.');
      return new Promise<string>((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = () => reject(new Error('The scene image could not be read.')); reader.readAsDataURL(blob); });
    })();
    if (imageBytes.size >= 24) imageBytes.delete(imageBytes.keys().next().value!);
    imageBytes.set(url, pending);
    void pending.catch(() => { if (imageBytes.get(url) === pending) imageBytes.delete(url); });
  }
  return pending;
}

export interface SceneColorRaster { pixels: ImageData; width: number; height: number }

export function rasterizeDisplayedCanvas(source: HTMLCanvasElement): SceneColorRaster {
  const scale=Math.min(1,1080/Math.max(source.width,source.height)), width=Math.max(1,Math.round(source.width*scale)),height=Math.max(1,Math.round(source.height*scale));
  const canvas=document.createElement('canvas');canvas.width=width;canvas.height=height;
  const context=canvas.getContext('2d',{willReadFrequently:true});if(!context)throw new Error('Color sampling is unavailable.');
  context.drawImage(source,0,0,width,height);const pixels=context.getImageData(0,0,width,height);canvas.width=0;canvas.height=0;return {pixels,width,height};
}

export function renderedScenePixel(raster: SceneColorRaster | null, bounds: { left: number; top: number; width: number; height: number }, clientX: number, clientY: number): Uint8ClampedArray | null {
  if (!raster || bounds.width <= 0 || bounds.height <= 0) return null;
  const x = Math.floor((clientX - bounds.left) * raster.width / bounds.width), y = Math.floor((clientY - bounds.top) * raster.height / bounds.height);
  if (x < 0 || y < 0 || x >= raster.width || y >= raster.height) return null;
  const offset = (y * raster.width + x) * 4;
  return raster.pixels.data.subarray(offset, offset + 4);
}

/** Snapshot actual displayed SVG scenery; the browser remains the sole renderer.
 * Saved/local painting is sampled separately through its existing ordered raster. */
export async function rasterizeDisplayedScene(svg: SVGSVGElement, options: { includeArtwork?: boolean } = {}): Promise<SceneColorRaster> {
  const bounds = svg.getBoundingClientRect(), scale = Math.min(1, 1080 / Math.max(bounds.width, bounds.height));
  const width = Math.max(1, Math.round(bounds.width * scale)), height = Math.max(1, Math.round(bounds.height * scale));
  const clone = svg.cloneNode(true) as SVGSVGElement;
  const originalObjects = [...svg.querySelectorAll('foreignObject')], copiedObjects = [...clone.querySelectorAll('foreignObject')];
  for (let index = 0; index < copiedObjects.length; index++) {
    const foreign = copiedObjects[index], canvas = originalObjects[index].querySelector('canvas');
    if (!options.includeArtwork || !canvas || !canvas.width || !canvas.height) { foreign.remove(); continue; }
    const image = document.createElementNS('http://www.w3.org/2000/svg','image');
    for (const attribute of ['x','y','width','height','transform','clip-path','opacity']) { const value = foreign.getAttribute(attribute); if (value !== null) image.setAttribute(attribute,value); }
    image.setAttribute('href',canvas.toDataURL('image/png')); image.setAttribute('preserveAspectRatio','none');
    foreign.replaceWith(image);
  }
  for (const element of clone.querySelectorAll(options.includeArtwork ? '[data-editor-overlay]' : '[data-paint-layer],[data-editor-overlay]')) element.remove();
  await Promise.all([...clone.querySelectorAll('image')].map(async element => {
    const href = element.getAttribute('href') || element.getAttributeNS('http://www.w3.org/1999/xlink', 'href') || '';
    const embedded = await inlineDisplayedImage(href); element.setAttribute('href', embedded); element.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
  }));
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg'); clone.setAttribute('width', String(width)); clone.setAttribute('height', String(height));
  const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml' }));
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => { const source = new Image(); source.onload = () => resolve(source); source.onerror = () => reject(new Error('The displayed scene could not be sampled.')); source.src = url; });
    const canvas = document.createElement('canvas'); canvas.width = width; canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true }); if (!context) throw new Error('Color sampling is unavailable.');
    context.drawImage(image, 0, 0, width, height);
    const pixels = context.getImageData(0, 0, width, height); canvas.width = 0; canvas.height = 0;
    return { pixels, width, height };
  } finally { URL.revokeObjectURL(url); }
}
